import { useCallback, useId, useRef, useState } from 'react';
import classNames from 'classnames';
import { useCloseOnOutsideInteraction } from 'hooks/useCloseOnOutsideInteraction';
import theme from 'config/theme';
import Icon, { IconNames } from 'components/Icon';
import { SelectFieldContainer, SelectOptionsContainer } from 'components/FinalForm/styled';

interface Option {
  label: string;
  value: string;
  isGroup?: boolean;
  children?: Array<{ label: string; value: string }>;
}

interface Props {
  options: Option[];
  label: string;
  value: string[];
  onChange: (next: string[]) => void;
}

export const MultiSelectField: React.FC<Props> = ({ options, label, value, onChange }) => {
  const [showOptions, setShowOptions] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement | null>(null);
  const listboxId = useId();
  const handleClose = useCallback(() => setShowOptions(false), []);
  useCloseOnOutsideInteraction(containerRef, showOptions, handleClose);

  const currentValues = value || [];

  const toggleGroup = (groupValue: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupValue)) {
        next.delete(groupValue);
      } else {
        next.add(groupValue);
      }
      return next;
    });
  };

  const handleSelectOption = (option: Option) => {
    let newValues: string[];

    if (option.isGroup && option.children) {
      // Selecting a group - add the group value and all children
      const childValues = option.children.map(child => child.value);
      const allGroupValues = [option.value, ...childValues];

      // Check if all are already selected
      const allSelected = allGroupValues.every(v => currentValues.includes(v));

      if (allSelected) {
        // Remove all
        newValues = currentValues.filter(v => !allGroupValues.includes(v));
      } else {
        // Add all that aren't already selected
        const toAdd = allGroupValues.filter(v => !currentValues.includes(v));
        newValues = [...currentValues, ...toAdd];
      }
    } else {
      // Selecting an individual option
      if (currentValues.includes(option.value)) {
        newValues = currentValues.filter((v: string) => v !== option.value);
      } else {
        newValues = [...currentValues, option.value];
      }
    }

    onChange(newValues);
  };

  const isSelected = (optionValue: string) => currentValues.includes(optionValue);

  const isGroupFullySelected = (option: Option) => {
    if (!option.isGroup || !option.children) return false;
    const childValues = option.children.map(child => child.value);
    return [option.value, ...childValues].every(v => currentValues.includes(v));
  };

  const isGroupPartiallySelected = (option: Option) => {
    if (!option.isGroup || !option.children) return false;
    const childValues = option.children.map(child => child.value);
    const allValues = [option.value, ...childValues];
    const selectedCount = allValues.filter(v => currentValues.includes(v)).length;
    return selectedCount > 0 && selectedCount < allValues.length;
  };

  const onSelectToggle = () => {
    setShowOptions(prev => !prev);
  };

  const selectedCount = currentValues.length;
  const displayText = selectedCount > 0 ? `${label} (${selectedCount})` : label;

  return (
    <SelectFieldContainer ref={containerRef}>
      <div className={classNames('search-select-field', { active: showOptions })}>
        <button
          type="button"
          className="row position-relative filter-button"
          onClick={onSelectToggle}
          aria-expanded={showOptions}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          aria-label={`${label}: ${displayText}`}
        >
          <span className="body-small-text search-select-input">{displayText}</span>
          <Icon
            name={showOptions ? IconNames.ChevronUp : IconNames.ChevronDown}
            color={theme.colors.white}
            size={30}
            className={classNames('select-option-icon', {
              'bg-oath-blue': showOptions,
              'bg-ink-blue': !showOptions,
            })}
          />
        </button>

        {showOptions && (
          <div>
            <SelectOptionsContainer id={listboxId} role="listbox" aria-multiselectable="true">
              {options.map(option => (
                <div key={option.value}>
                  <div
                    role="option"
                    aria-selected={
                      isGroupFullySelected(option) ||
                      isGroupPartiallySelected(option) ||
                      isSelected(option.value)
                    }
                    className={classNames('select-option', {
                      selected: isGroupFullySelected(option) || isSelected(option.value),
                      'partially-selected': isGroupPartiallySelected(option),
                    })}
                    onClick={() => handleSelectOption(option)}
                  >
                    <span className="body-small-text color-inherit">
                      {option.label}
                      {isGroupFullySelected(option) && ' ✓'}
                      {isGroupPartiallySelected(option) && ' -'}
                    </span>
                    {option.isGroup && (
                      <span
                        onClick={e => {
                          e.stopPropagation();
                          toggleGroup(option.value);
                        }}
                        style={{ cursor: 'pointer', marginLeft: '8px' }}
                      >
                        <Icon
                          name={
                            expandedGroups.has(option.value)
                              ? IconNames.ChevronUp
                              : IconNames.ChevronDown
                          }
                          size={16}
                        />
                      </span>
                    )}
                  </div>

                  {option.isGroup && option.children && expandedGroups.has(option.value) && (
                    <div style={{ paddingLeft: '20px' }}>
                      {option.children.map(child => (
                        <div
                          key={child.value}
                          role="option"
                          aria-selected={isSelected(child.value)}
                          className={classNames('select-option', {
                            selected: isSelected(child.value),
                          })}
                          onClick={() => handleSelectOption(child)}
                        >
                          <span className="body-small-text color-inherit">
                            {child.label}
                            {isSelected(child.value) && ' ✓'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </SelectOptionsContainer>
          </div>
        )}
      </div>
    </SelectFieldContainer>
  );
};
